import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Phone, IdentificationBadge, EnvelopeSimple, ArrowRight, CheckCircle, LockKey, Eye, EyeSlash } from "@phosphor-icons/react";
import { teleproApi, TELEPRO_TOKEN_KEY } from "../lib/teleproApi";

const PENDING_KEY = "mc68_telepro_verify_pending";

/**
 * /teleprospecteur — connexion télépro.
 *
 * MODE PRINCIPAL (par défaut) : connexion directe par code (ex: JULIE-010).
 * MODE FALLBACK : lien magique par email (au cas où le telepro a oublié son code).
 *
 * Pourquoi ce changement : les emails Resend tombent dans les SPAM Hotmail/Outlook,
 * ce qui empêchait des telepros de se connecter. Le code est plus fiable, plus rapide,
 * et le contenu du portail est lui-même non-sensible (pas d'accès admin).
 *
 * Le flow ?token=... (lien magique) reste supporté pour rétrocompatibilité.
 */
export default function TeleproLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState("code"); // "code" | "email" | "password" | "setup-password" | "forgot"
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [codeError, setCodeError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  // Vague 31 — Self-service forgot-password
  const [forgotIdentifier, setForgotIdentifier] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  // Auto-flows: existing JWT → portal | ?token= → verify
  useEffect(() => {
    const existingJwt = localStorage.getItem(TELEPRO_TOKEN_KEY);
    if (existingJwt) {
      navigate("/teleprospecteur/portail", { replace: true });
      return;
    }
    const token = searchParams.get("token");
    if (!token) return;
    if (sessionStorage.getItem(PENDING_KEY) === token) return;
    sessionStorage.setItem(PENDING_KEY, token);

    setVerifying(true);
    teleproApi.post("/verify", { token })
      .then((r) => {
        // VAGUE 27 — Si le télépro n'a pas de mot de passe (première connexion
        // OU reset admin), on bascule directement sur l'écran de création de
        // mot de passe au lieu de le loguer en JWT.
        if (r.data?.needs_password_setup) {
          setCode((r.data.code || "").toUpperCase());
          setMode("setup-password");
          setVerifying(false);
          toast.info("Crée un nouveau mot de passe pour finaliser ton accès");
          return;
        }
        localStorage.setItem(TELEPRO_TOKEN_KEY, r.data.token);
        toast.success(`Bienvenue ${r.data.telepro?.first_name || ""} !`);
        navigate("/teleprospecteur/portail", { replace: true });
      })
      .catch((err) => {
        setVerifyError(err?.response?.data?.detail || "Lien expiré ou déjà utilisé");
        setVerifying(false);
      });
  }, [searchParams, navigate]);

  const onSubmitCode = async (e) => {
    e.preventDefault();
    const cleaned = code.trim().toUpperCase().replace(/\s+/g, "");
    if (!cleaned) return;
    setSubmitting(true);
    setCodeError("");
    try {
      const r = await teleproApi.post("/login-by-code", { code: cleaned });
      if (r.data.needs_password_setup) {
        setCode(cleaned);
        setMode("setup-password");
      } else if (r.data.token) {
        // Legacy path (no password ever set) — should not happen post-migration.
        localStorage.setItem(TELEPRO_TOKEN_KEY, r.data.token);
        toast.success(`Bienvenue ${r.data.telepro?.first_name || ""} !`);
        navigate("/teleprospecteur/portail", { replace: true });
      } else {
        setCode(cleaned);
        setMode("password");
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Code invalide.";
      // 401 with "Mot de passe requis" → the code is valid but password needed
      if (status === 401 && /mot de passe/i.test(msg)) {
        setCode(cleaned);
        setMode("password");
      } else {
        setCodeError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitPassword = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    setPasswordError("");
    try {
      const r = await teleproApi.post("/login-by-code", { code, password });
      localStorage.setItem(TELEPRO_TOKEN_KEY, r.data.token);
      toast.success(`Bienvenue ${r.data.telepro?.first_name || ""} !`);
      navigate("/teleprospecteur/portail", { replace: true });
    } catch (err) {
      setPasswordError(err?.response?.data?.detail || "Mot de passe incorrect.");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitSetupPassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setPasswordError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== passwordConfirm) {
      setPasswordError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    setPasswordError("");
    try {
      const r = await teleproApi.post("/set-password", { code, password, password_confirm: passwordConfirm });
      localStorage.setItem(TELEPRO_TOKEN_KEY, r.data.token);
      toast.success(`Mot de passe créé ! Bienvenue ${r.data.telepro?.first_name || ""} !`);
      navigate("/teleprospecteur/portail", { replace: true });
    } catch (err) {
      setPasswordError(err?.response?.data?.detail || "Erreur — réessaie.");
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const r = await teleproApi.post("/login-request", { email: email.trim().toLowerCase() });
      // Le magic-link automatique est désactivé (spam télépros). On informe
      // l'utilisateur qu'il doit passer par son mot de passe, ou contacter
      // l'admin pour un lien manuel en cas d'oubli.
      if (r?.data?.reason === "magic_link_disabled") {
        toast.info(r.data.message || "Connecte-toi avec ton mot de passe. Contacte l'admin en cas d'oubli.");
        setMode("code");
      } else {
        setSent(true);
      }
    } catch {
      toast.error("Erreur réseau. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  // Vague 31 — Self-service "Mot de passe oublié" (code OU email accepté).
  const onSubmitForgot = async (e) => {
    e.preventDefault();
    const id = forgotIdentifier.trim();
    if (!id) return;
    setSubmitting(true);
    try {
      await teleproApi.post("/forgot-password", { identifier: id });
      // Toujours "neutre" — on ne dit jamais si le compte existe.
      setForgotSent(true);
    } catch {
      toast.error("Erreur réseau. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6">
        <div className="text-center" data-testid="telepro-verifying">
          <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3"/>
          <p className="text-muted-foreground">Vérification en cours…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] grid place-items-center px-6 py-12">
      <Helmet>
        <title>Portail Téléprospecteurs — MonCommerce68</title>
        <meta name="robots" content="noindex,nofollow"/>
      </Helmet>
      <div className="w-full max-w-md bg-card border border-border rounded-sm p-8 shadow-sm" data-testid="telepro-login-card">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-3">
            <Phone size={20} weight="duotone"/>
          </div>
          <div className="label-uppercase mb-1">Portail Téléprospecteurs</div>
          <h1 className="font-display text-2xl font-medium leading-tight">Connexion rapide</h1>
        </div>

        {verifyError && (
          <div className="mb-4 text-xs text-secondary bg-secondary/10 border border-secondary/30 rounded-sm p-3" data-testid="telepro-verify-error">
            {verifyError}.
          </div>
        )}

        {mode === "code" && (
          <form onSubmit={onSubmitCode} className="space-y-4" data-testid="telepro-code-form">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Entre ton code téléprospecteur (ex : <code className="bg-accent px-1 rounded font-mono">JULIE-010</code>) pour accéder à ton portail immédiatement.
            </p>
            <div>
              <label htmlFor="tp-code" className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1 block">
                Ton code téléprospecteur
              </label>
              <div className="relative">
                <IdentificationBadge size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                <input
                  id="tp-code"
                  type="text"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="JULIE-010"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck="false"
                  className="w-full border border-border rounded-sm pl-9 pr-3 py-2.5 text-sm bg-background focus:border-primary outline-none font-mono uppercase tracking-wider"
                  data-testid="telepro-code-input"
                />
              </div>
              {codeError && <p className="text-xs text-destructive mt-2" data-testid="telepro-code-error">{codeError}</p>}
            </div>
            <button
              type="submit"
              disabled={submitting || !code.trim()}
              className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="telepro-submit-code"
            >
              {submitting ? "Connexion…" : <>Se connecter <ArrowRight size={14} weight="bold"/></>}
            </button>
            <div className="text-center pt-2 text-[11px] text-muted-foreground">
              Oublié ton code ou ton mot de passe ?<br/>
              Contacte l'admin (<a href="mailto:digitalcorpfrance@gmail.com" className="text-primary hover:underline">digitalcorpfrance@gmail.com</a>) — il t'enverra un lien de secours.
            </div>
          </form>
        )}

        {mode === "password" && (
          <form onSubmit={onSubmitPassword} className="space-y-4" data-testid="telepro-password-form">
            <div className="text-xs text-muted-foreground bg-accent/30 border border-border rounded-sm p-3">
              Code <span className="font-mono font-bold text-foreground">{code}</span> reconnu. Entre ton mot de passe personnel pour te connecter.
            </div>
            <div>
              <label htmlFor="tp-pwd" className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1 block">
                Ton mot de passe
              </label>
              <div className="relative">
                <LockKey size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                <input
                  id="tp-pwd"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full border border-border rounded-sm pl-9 pr-10 py-2.5 text-sm bg-background focus:border-primary outline-none"
                  data-testid="telepro-password-input"
                  autoFocus
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1} data-testid="telepro-password-toggle">
                  {showPassword ? <EyeSlash size={14}/> : <Eye size={14}/>}
                </button>
              </div>
              {passwordError && <p className="text-xs text-destructive mt-2" data-testid="telepro-password-error">{passwordError}</p>}
            </div>
            <button
              type="submit"
              disabled={submitting || !password}
              className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="telepro-submit-password"
            >
              {submitting ? "Connexion…" : <>Se connecter <ArrowRight size={14} weight="bold"/></>}
            </button>
            <div className="text-center space-y-2 pt-2">
              <button
                type="button"
                onClick={() => { setMode("code"); setPassword(""); setPasswordError(""); }}
                className="text-[11px] text-muted-foreground hover:text-primary block mx-auto"
                data-testid="telepro-back-to-code"
              >
                ← Changer de code
              </button>
              <p className="text-[11px] text-muted-foreground">
                <button
                  type="button"
                  onClick={() => {
                    setForgotIdentifier(code || "");
                    setForgotSent(false);
                    setMode("forgot");
                  }}
                  className="underline decoration-2 underline-offset-2 text-primary hover:opacity-80 font-medium"
                  data-testid="telepro-forgot-password-open"
                >
                  Mot de passe oublié ?
                </button>
              </p>
            </div>
          </form>
        )}

        {mode === "setup-password" && (
          <form onSubmit={onSubmitSetupPassword} className="space-y-4" data-testid="telepro-setup-password-form">
            <div className="text-xs bg-primary/10 border border-primary/30 rounded-sm p-3 text-foreground">
              <div className="font-bold text-primary mb-1">🔐 Première connexion</div>
              Crée ton mot de passe personnel pour sécuriser ton compte <span className="font-mono font-bold">{code}</span>. Tu l'utiliseras à chaque connexion. Min. 8 caractères.
            </div>
            <div>
              <label htmlFor="tp-pwd-new" className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1 block">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <LockKey size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                <input
                  id="tp-pwd-new"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 caractères"
                  autoComplete="new-password"
                  className="w-full border border-border rounded-sm pl-9 pr-10 py-2.5 text-sm bg-background focus:border-primary outline-none"
                  data-testid="telepro-setup-password-input"
                  autoFocus
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                  {showPassword ? <EyeSlash size={14}/> : <Eye size={14}/>}
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="tp-pwd-confirm" className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1 block">
                Confirme le mot de passe
              </label>
              <div className="relative">
                <LockKey size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                <input
                  id="tp-pwd-confirm"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={8}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="Retape ton mot de passe"
                  autoComplete="new-password"
                  className="w-full border border-border rounded-sm pl-9 pr-3 py-2.5 text-sm bg-background focus:border-primary outline-none"
                  data-testid="telepro-setup-password-confirm"
                />
              </div>
              {passwordError && <p className="text-xs text-destructive mt-2" data-testid="telepro-setup-password-error">{passwordError}</p>}
            </div>
            <button
              type="submit"
              disabled={submitting || password.length < 8 || password !== passwordConfirm}
              className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="telepro-submit-setup-password"
            >
              {submitting ? "Création…" : <>Créer mon mot de passe <ArrowRight size={14} weight="bold"/></>}
            </button>
            <button
              type="button"
              onClick={() => { setMode("code"); setPassword(""); setPasswordConfirm(""); setPasswordError(""); }}
              className="text-[11px] text-muted-foreground hover:text-primary block mx-auto"
            >
              ← Retour
            </button>
          </form>
        )}

        {mode === "email" && (
          sent ? (
            <div className="text-center py-4" data-testid="telepro-magic-sent">
              <CheckCircle size={32} weight="duotone" className="text-primary mx-auto mb-3"/>
              <p className="text-sm font-medium mb-2">Si ton email est enregistré, tu recevras un lien dans 1-2 minutes.</p>
              <p className="text-xs text-muted-foreground mb-4">Vérifie aussi tes spams. Le lien expire dans 30 minutes.</p>
              <button
                onClick={() => { setSent(false); setMode("code"); setEmail(""); }}
                className="text-xs text-primary hover:underline"
                data-testid="telepro-resend"
              >
                Revenir à la connexion par code
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmitEmail} className="space-y-4" data-testid="telepro-email-form">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Saisis l&apos;email associé à ton compte. Si tu ne reçois rien (Hotmail/Outlook filtrent parfois), reviens à la connexion par code.
              </p>
              <div>
                <label htmlFor="tp-email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1 block">
                  Ton email
                </label>
                <div className="relative">
                  <EnvelopeSimple size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                  <input
                    id="tp-email"
                    type="email"
                    inputMode="email"
                    autoCapitalize="off"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="prenom.nom@example.com"
                    autoComplete="email"
                    className="w-full border border-border rounded-sm pl-9 pr-3 py-3 text-base sm:text-sm sm:py-2.5 bg-background focus:border-primary outline-none min-h-[44px]"
                    data-testid="telepro-email-input"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting || !email.trim()}
                className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="telepro-submit"
              >
                {submitting ? "Envoi…" : <>Recevoir mon lien <ArrowRight size={14} weight="bold"/></>}
              </button>
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => setMode("code")}
                  className="text-[11px] text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                  data-testid="telepro-switch-to-code"
                >
                  <IdentificationBadge size={12}/> Revenir à la connexion par code
                </button>
              </div>
            </form>
          )
        )}

        {/* Vague 31 — Self-service "Mot de passe oublié" */}
        {mode === "forgot" && (
          forgotSent ? (
            <div className="text-center py-4" data-testid="telepro-forgot-password-sent">
              <CheckCircle size={32} weight="duotone" className="text-primary mx-auto mb-3"/>
              <p className="text-sm font-medium mb-2">Demande enregistrée.</p>
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                Si un compte télépro est associé à cette référence, un email
                avec un lien de réinitialisation vient d&apos;être envoyé.
                Il expire dans <b>60 minutes</b>. Pense à regarder tes spams.
              </p>
              <button
                onClick={() => { setForgotSent(false); setForgotIdentifier(""); setMode("code"); }}
                className="text-xs text-primary hover:underline"
                data-testid="telepro-forgot-password-back"
              >
                Revenir à la connexion
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmitForgot} className="space-y-4" data-testid="telepro-forgot-password-form">
              <div className="text-xs bg-primary/5 border border-primary/20 rounded-sm p-3 leading-relaxed">
                <div className="font-bold text-primary mb-1">🔐 Mot de passe oublié</div>
                Entre ton <b>code télépro</b> (ex: <code className="bg-accent px-1 rounded font-mono">JULIE-010</code>) <b>ou ton email</b>. On t&apos;envoie un lien pour créer un nouveau mot de passe.
              </div>
              <div>
                <label htmlFor="tp-forgot-id" className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1 block">
                  Code ou email
                </label>
                <div className="relative">
                  <IdentificationBadge size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                  <input
                    id="tp-forgot-id"
                    type="text"
                    required
                    value={forgotIdentifier}
                    onChange={(e) => setForgotIdentifier(e.target.value)}
                    placeholder="JULIE-010 ou julie@example.com"
                    autoComplete="username"
                    className="w-full border border-border rounded-sm pl-9 pr-3 py-3 text-base sm:text-sm sm:py-2.5 bg-background focus:border-primary outline-none min-h-[44px]"
                    data-testid="telepro-forgot-password-input"
                    autoFocus
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting || !forgotIdentifier.trim()}
                className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="telepro-forgot-password-submit"
              >
                {submitting ? "Envoi…" : <>Envoyer le lien <ArrowRight size={14} weight="bold"/></>}
              </button>
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => { setMode("code"); setForgotIdentifier(""); }}
                  className="text-[11px] text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                  data-testid="telepro-forgot-password-cancel"
                >
                  ← Annuler
                </button>
              </div>
            </form>
          )
        )}

        <div className="mt-6 pt-4 border-t border-border text-center space-y-2">
          <div className="text-xs">
            <span className="text-muted-foreground">Pas encore inscrit ? </span>
            <Link to="/teleprospecteur/inscription" className="text-primary font-semibold hover:underline" data-testid="telepro-signup-link">
              Rejoins l&apos;équipe
            </Link>
          </div>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-block" data-testid="telepro-back-home">
            ← Retour au site
          </Link>
        </div>
      </div>
    </div>
  );
}
